import { cachedFetch, invalidateKey, peekCache, seedCache } from './queryCache.tsx';
import { showUser, showUserById } from '../services/admin/UserService.tsx';
import { Users } from '../models/User.tsx';

/** Perfil del layout admin: fresco ~30s, SWR hasta 5min. */
const VISITED_USER_LIVE_TTL_MS = 30_000;
const VISITED_USER_LIVE_STALE_MS = 5 * 60_000;

const keys = {
  profile: (userName: string) => `adminVisitedUser:${userName}`,
  preview: (userName: string) => `adminVisitedUserPreview:${userName}`,
};

function fetchOpts(force = false) {
  return {
    ttlMs: VISITED_USER_LIVE_TTL_MS,
    staleMs: VISITED_USER_LIVE_STALE_MS,
    ...(force ? { force: true as const } : {}),
  };
}

function load<T>(key: string, fetcher: () => Promise<T>, force = false): Promise<T> {
  return cachedFetch(key, fetcher, fetchOpts(force));
}

function peek<T>(key: string): T | undefined {
  return peekCache<T>(key);
}

export type VisitedUserNavState = {
  from?: string;
  userId?: string;
  visitedUserPreview?: Users;
};

export function buildVisitedUserNavState(
  user: Users,
  extra?: Pick<VisitedUserNavState, 'from'>
): VisitedUserNavState {
  return {
    ...extra,
    userId: user.id,
    visitedUserPreview: user,
  };
}

/** Preview ligero de listas (no bloquea el fetch del perfil completo). */
export function seedVisitedUserPreview(userName: string, user: Users) {
  if (!userName || !user) return;
  seedCache(keys.preview(userName), { ...user, id: user.id });
}

export function peekVisitedUserPreview(userName: string): Users | undefined {
  return peek<Users>(keys.preview(userName));
}

export function peekVisitedUserProfile(userName: string): Users | undefined {
  return peek<Users>(keys.profile(userName));
}

/** Perfil en UI: solo datos ya confirmados en esta sesión de visita (post-fetch). */
export function peekVisitedUserProfileForDisplay(userName: string): Users | undefined {
  return peekVisitedUserProfile(userName);
}

export function visitedUserProfileCacheKey(userName: string): string {
  return keys.profile(userName);
}

export function seedVisitedUserProfile(userName: string, user: Users) {
  if (!userName || !user) return;
  seedCache(keys.profile(userName), { ...user, id: user.id });
}

export function peekVisitedUser(userName: string): Users | undefined {
  return peekVisitedUserProfile(userName) ?? peekVisitedUserPreview(userName);
}

export function loadVisitedUserProfile(userName: string, force = true, userId?: string): Promise<Users> {
  return load(
    keys.profile(userName),
    () => (userId ? showUserById(userId, { fromServer: true }) : showUser(userName, { fromServer: true })),
    force
  );
}

export function invalidateVisitedUser(userName: string) {
  invalidateKey(keys.profile(userName));
  invalidateKey(keys.preview(userName));
}

/** Patch cached profile/preview after Customer Admin (or other list) edits. */
export function patchVisitedUserCaches(userName: string, updates: Partial<Users>) {
  if (!userName) return;
  const profile = peekVisitedUserProfile(userName);
  if (profile) seedVisitedUserProfile(userName, { ...profile, ...updates });
  const preview = peekVisitedUserPreview(userName);
  if (preview) seedVisitedUserPreview(userName, { ...preview, ...updates });
}
